import { useForm } from '@xmanscript/useform';
import OTPInput from 'react-otp-input';
import { ScaleLoader } from 'react-spinners';
import { ArrowRightIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Container from '@/ui/_lib/Container';
import { ReactComponent as TextIconLogo } from '@/assets/image/text-icon-logo.svg';
import Button from '@/ui/_lib/Button';
import { LoginService, VerifyOTPService } from '@/ui/_apiUrls';
import { cn } from '@/utils';
import ErrorLabel from '@/ui/_lib/FormControl/_lib/ErrorLabel';
import { ReactComponent as LogoText } from '@/assets/image/Logo-text.svg';
import useAuth from '@/hooks/useAuth';
import LogoutButton from '@/ui/components/LogoutButton';

export default function OTP() {
  const { logIn, useNonAuthenticatedPage } = useAuth();
  useNonAuthenticatedPage();

  const [time, setTime] = useState<number>(Number(localStorage.getItem('otpTime')) || 60);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    mutate: verifyOTP,
    isLoading: isVerifyingOTP,
    error: verifyOTPError,
    isError,
  } = VerifyOTPService.post({
    mutationParams: {
      onSuccess: (response) => {
        logIn(response.user);
      },
    },
  });

  const { register, onSubmitHandler } = useForm({
    formName: 'otpForm',
    touchOnChange: true,
    initialValues: { otpCode: '', email: searchParams.get('email') },
    submitHandler: async (vals) => {
      try {
        verifyOTP(vals.currentPacket);
      } catch (err: any) {
        throw new Error(err);
      }
    },
    persistValues: false,
  });

  useEffect(() => {
    const persistedTime = localStorage.getItem('otpTime');
    if (persistedTime) {
      setTime(Number(persistedTime));
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime((prev) => {
        if (prev === 0) {
          clearInterval(interval);
          localStorage.setItem('otpTime', '0');
          return 0;
        }
        localStorage.setItem('otpTime', (prev - 1).toString());
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [time]);

  const { mutate, isLoading: isReSendingOTP } = LoginService.post({
    mutationParams: {
      onSuccess: () => {
        setTime(60);
      },
    },
  });

  // if (isAuthenticated()) {
  //   return <AlreadyLoggedIn />;
  // }

  return (
    <Container className="z-50 flex flex-col items-center justify-center gap-[5rem] bg-[#F8F8FF] bg-opacity-100">
      <div className="absolute top-4 right-4">
        <LogoutButton variant="primary" className="px-4 py-2 text-sm" />
      </div>
      {!searchParams.get('email') ? (
        <div className="flex h-[60%] w-full flex-col items-center justify-around">
          <div className="flex flex-col items-center justify-center gap-4 ">
            <h1 className="text-[2rem] font-semibold text-red-400 md:text-[5rem]">Invalid Link</h1>
            <Button
              onClick={() => {
                navigate('/');
              }}
              className="w-fit"
              variant="primary"
            >
              Back to home page
            </Button>
          </div>

          <LogoText />
        </div>
      ) : (
        <>
          <TextIconLogo />
          <div className=" flex w-full flex-col items-center justify-center gap-4">
            <p
              className={cn(
                'text-center text-[2rem] font-bold leading-[2.5rem]',
                time === 0 ? 'text-[#dfdfdf]' : 'text-[#475467]',
              )}
            >
              00:{time}
            </p>
            <p className=" flex flex-col text-center font-inter text-[1rem] font-normal">
              Enter the verification code we’ve sent you on{' '}
              <span className=" font-semibold text-[#039BE5]">{searchParams?.get('email')}</span>
            </p>
          </div>
          <form
            onSubmit={onSubmitHandler}
            className="heading flex flex-col items-center justify-center gap-[60px] md:flex-col lg:pt-0"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <OTPInput
                {...register('otpCode')}
                numInputs={6}
                inputType="text"
                renderSeparator={<span>&nbsp;&nbsp;&nbsp;</span>}
                renderInput={(props) => <input {...props} />}
                inputStyle="!w-[30px] !h-[30px] md:!w-[70px] md:!h-[70px]   rounded-lg bg-[#475467] text-[#ffffff] text-center font-semibold text-[18px] md:text-[28px]"
              />
              {time === 0 && (
                <Button
                  className="flex items-center justify-center gap-2 text-[0.8rem] underline"
                  variant="link"
                  style={{ boxShadow: 'none' }}
                  onClick={() => {
                    mutate({ email: searchParams.get('email') });
                  }}
                >
                  <p>Resend{isReSendingOTP ? 'ing' : ''} OTP</p>{' '}
                  {isReSendingOTP && <ScaleLoader height={20} color="#36d7b7" />}
                </Button>
              )}
            </div>
            <div className="flex flex-col items-center justify-center gap-2">
              {time !== 0 && (
                <Button
                  variant="link"
                  type="submit"
                  style={{ boxShadow: 'none' }}
                  className="flex w-[8rem] items-center justify-center gap-2 whitespace-nowrap text-[#039BE5]"
                >
                  Log In
                  {isVerifyingOTP ? <ScaleLoader height={20} color="#36d7b7" /> : <ArrowRightIcon />}
                </Button>
              )}
              {!isVerifyingOTP && isError && (
                <ErrorLabel
                  className="capitalize"
                  message={verifyOTPError?.message || 'Some error occurred. Please try again.'}
                />
              )}
            </div>
          </form>
        </>
      )}
    </Container>
  );
}